<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getAccountingStepByStepCoop } from "../data/getAccountingStepByStepCoop.js";
	import { getAccountingWhyFastest } from "../data/getAccountingWhyFastest.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsAccounting } from "../data/getFaqsAccounting.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getProsAccounting } from "../data/getProsAccounting.js";
	import type { AccountingProps } from "./AccountingProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: AccountingProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Konečně budete cg[[účetnictví řešit online]] a bez úmorného papírování" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Co dřív dělali čtyři lidé, dnes bez problému sp[[zvládne jeden]]. Stačí, když vám sp[[vytvoříme řešení na míru]], které digitalizuje a sp[[automatizuje účetnictví]] i objednávky, sp[[zrychlí práci]] a sp[[propojí všechny systémy]] tak, aby spolu bezchybně komunikovaly."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci fastest služby" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="cg[[Vyvineme]] fakturační systém i cg[[automatizujeme]] proces objednávek" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Hromadění papírových dokladů, práce s fakturačním softwarem „z roku raz dva“ nebo chaotické zadávání finančních dat do různých systémů. sp[[Zní vám to povědomě?]]"
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Pomůžeme vám]] převést účetnictví i zpracování objednávek sp[[do 21. století]]. Ušetříte sp[[čas]], sp[[personální náklady]] nebo sp[[výdaje za tisk]]. Zajistíme také soulad s legislativou a postaráme se, aby mezi sebou všechny systémy sp[[správně komunikovaly]]."
				/>
			</p>
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getProsAccounting(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Poslat poptávku
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] svěřit digitalizaci a automatizaci účetnictví právě cg[[Fastest?]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getAccountingWhyFastest(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Pro cg[[Tymbe]] jsme zajistili vše od náboru po cg[[online mzdové účetnictví]]" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Accounting} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section gridHeadline="container-8" noPadding="bot" centered>
		{#snippet headline()}
			<Formatted text="cg[[Kdo]] další díky Fastest cg[[zrychlil]] růst podnikání?" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Accounting)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[Jak probíhá]] typická spolupráce" />
			<span class="block text-2md font-light mt-6"
				><Formatted
					text="Ať už potřebujete připravit cg[[účetní software na míru]] nebo kompletně sp[[digitalizovat]] a cg[[automatizovat]] finanční a objednávkové procesy, celým procesem vás krok za krokem cg[[provedeme]]."
				/></span
			>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getAccountingStepByStepCoop(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k vývoji v oblasti cg[[účetnictví]]" />
		{/snippet}
		<FaqItems faqs={getFaqsAccounting(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
